import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import IMask from 'imask';
import { useTranslation } from 'next-i18next';
import { Checkbox, Input, TextField } from '@zep/ui';
import { numberRegex } from '../../../../utils';
import { MaskedInput } from '../../../common/MaskedInput';
import { RegisterPaymentSchemaSpec, RegisterPaymentSchemaType, SubscriptionTKey } from './schema/PaymentCardSchema';
const _PaymentForm = () => {
  return <></>;
};
const InputCardNumber = ({
  size = 'md'
}: {
  size: 'sm' | 'md';
}) => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    trigger,
    setValue,
    formState: {
      errors
    }
  } = useFormContext<RegisterPaymentSchemaType>();
  return <TextField size={size} label={t('subscription:payment.cardRegister.labelCardNumber')} helperText={errors.cardNumber?.message && t(errors.cardNumber?.message as SubscriptionTKey)} error={Boolean(errors.cardNumber)} data-sentry-element="TextField" data-sentry-component="InputCardNumber" data-sentry-source-file="PaymentForm.tsx">
      <MaskedInput size={size} mask="0000 0000 **** ****" definitions={{
      '*': {
        mask: '0',
        displayChar: '*'
      }
    }} overwrite="shift" placeholder={t('subscription:payment.cardRegister.placeHolderCardNumber')} onAccept={(value, {
      unmaskedValue
    }) => {
      setValue('cardNumber', unmaskedValue, {
        shouldDirty: true,
        shouldTouch: true
      });
    }} error={Boolean(errors.cardNumber)} onBlur={() => trigger('cardNumber')} data-sentry-element="MaskedInput" data-sentry-source-file="PaymentForm.tsx" />
    </TextField>;
};
const InputExpireDate = ({
  size
}: {
  size: 'sm' | 'md';
}) => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    getValues,
    setError,
    setValue,
    clearErrors,
    formState: {
      errors
    }
  } = useFormContext<RegisterPaymentSchemaType>();
  const triggerExpireDate = () => {
    const {
      expireDate
    } = getValues();
    if (!expireDate) {
      setError('expireDate', {
        type: 'required',
        message: RegisterPaymentSchemaSpec.expireDate.requiredMessage
      });
      return;
    }
    if (expireDate && expireDate.length < RegisterPaymentSchemaSpec.expireDate.minLength) {
      setError('expireDate', {
        type: 'required',
        message: RegisterPaymentSchemaSpec.expireDate.minLengthMessage
      });
      return;
    }
    clearErrors('expireDate');
  };
  return <TextField size={size} label={t('subscription:payment.cardRegister.labelExpirationPeriod')} error={Boolean(errors.expireDate)} helperText={errors.expireDate?.message && t(errors.expireDate.message as SubscriptionTKey)} data-sentry-element="TextField" data-sentry-component="InputExpireDate" data-sentry-source-file="PaymentForm.tsx">
      <MaskedInput size={size} mask="MM/YY" blocks={{
      MM: {
        mask: IMask.MaskedRange,
        from: 1,
        to: 12,
        autofix: true
      },
      YY: {
        mask: '00'
      }
    }} placeholder="mm/yy" onAccept={(value, {
      unmaskedValue
    }) => {
      setValue('expireDate', unmaskedValue, {
        shouldDirty: true,
        shouldTouch: true
      });
    }} error={Boolean(errors.expireDate)} onBlur={triggerExpireDate} data-sentry-element="MaskedInput" data-sentry-source-file="PaymentForm.tsx" />
    </TextField>;
};
const InputCardName = ({
  size
}: {
  size: 'sm' | 'md';
}) => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    watch,
    register,
    trigger,
    formState: {
      errors
    }
  } = useFormContext<RegisterPaymentSchemaType>();
  return <TextField size={size} label={t('subscription:payment.cardRegister.labelCardNickname')} error={Boolean(errors.name)} helperText={errors.name?.message && t(errors.name.message as SubscriptionTKey)} rightSlot={`${(watch('name') || '').length}/${RegisterPaymentSchemaSpec.name.maxLength}`} data-sentry-element="TextField" data-sentry-component="InputCardName" data-sentry-source-file="PaymentForm.tsx">
      <Input type="text" size={size} {...register('name')} error={Boolean(errors.name)} isFullWidth placeholder={t('subscription:payment.cardRegister.placeHolderCardNickname')} maxLength={RegisterPaymentSchemaSpec.name.maxLength} onBlur={() => trigger('name')} data-sentry-element="Input" data-sentry-source-file="PaymentForm.tsx" />
    </TextField>;
};
const InputBirthDate = ({
  size
}: {
  size: 'sm' | 'md';
}) => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    watch,
    setValue,
    trigger,
    formState: {
      errors
    }
  } = useFormContext<RegisterPaymentSchemaType>();
  const handleBirthDate = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || numberRegex.test(value)) {
      setValue('birthDate', value, {
        shouldDirty: true,
        shouldTouch: true
      });
    }
  };
  return <TextField size={size} label={t('subscription:payment.cardRegister.labelBirthDate')} error={Boolean(errors.birthDate)} helperText={errors.birthDate?.message && t(errors.birthDate.message as SubscriptionTKey)} data-sentry-element="TextField" data-sentry-component="InputBirthDate" data-sentry-source-file="PaymentForm.tsx">
      <Input size={size} type="text" value={watch('birthDate')} onChange={handleBirthDate} isFullWidth placeholder={t('subscription:payment.cardRegister.placeHolderBirthDate')} maxLength={RegisterPaymentSchemaSpec.birthDate.maxLength} error={Boolean(errors.birthDate)} onBlur={() => trigger('birthDate')} data-sentry-element="Input" data-sentry-source-file="PaymentForm.tsx" />
    </TextField>;
};
const InputIncNumber = ({
  size
}: {
  size: 'sm' | 'md';
}) => {
  const {
    setValue,
    trigger,
    formState: {
      errors
    }
  } = useFormContext<RegisterPaymentSchemaType>();
  const {
    t
  } = useTranslation(['subscription']);
  return <TextField size={size} label={t('subscription:payment.cardRegister.labelCorporationNumber')} error={Boolean(errors.businessNumber)} helperText={errors.businessNumber?.message && t(errors.businessNumber?.message as SubscriptionTKey)} data-sentry-element="TextField" data-sentry-component="InputIncNumber" data-sentry-source-file="PaymentForm.tsx">
      <MaskedInput size={size} mask="000-00-00000" overwrite="shift" placeholder={t('subscription:payment.cardRegister.placeHolderCorporationNumber')} onAccept={(value, {
      unmaskedValue
    }) => {
      setValue('businessNumber', unmaskedValue, {
        shouldDirty: true,
        shouldTouch: true
      });
    }} error={Boolean(errors.businessNumber)} onBlur={() => trigger('businessNumber')} data-sentry-element="MaskedInput" data-sentry-source-file="PaymentForm.tsx" />
    </TextField>;
};
const IncCheckBox = () => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    watch,
    clearErrors,
    resetField,
    getValues,
    setValue
  } = useFormContext<RegisterPaymentSchemaType>();
  const isInc = watch('isBusiness');
  // required 를 무시할수있게 유효한 값을 넣어 줍니다
  useEffect(() => {
    if (isInc) {
      clearErrors('birthDate');
      setValue('birthDate', '000410', {
        shouldDirty: true
      });
      setValue('businessNumber', '', {
        shouldDirty: true
      });
    } else {
      clearErrors('businessNumber');
      setValue('businessNumber', '0000000000', {
        shouldDirty: true
      });
      setValue('birthDate', '', {
        shouldDirty: true
      });
    }
  }, [isInc, setValue, clearErrors, resetField]);
  return <Checkbox label={t('subscription:payment.cardRegister.checkCorporationCard')} onChange={() => setValue('isBusiness', !getValues('isBusiness'))} data-sentry-element="Checkbox" data-sentry-component="IncCheckBox" data-sentry-source-file="PaymentForm.tsx" />;
};
export const PaymentForm = Object.assign(_PaymentForm, {
  InputCardNumber,
  InputExpireDate,
  InputIncNumber,
  InputBirthDate,
  InputCardName,
  IncCheckBox
});